import React, { FunctionComponent, useEffect, useState } from "react";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { NodeWithHierarchy, copyTree } from "@iventis/tree-browser";
import { Node } from "@iventis/domain-model/model/node";
import { StandardNodeContent } from "@iventis/plans/src/plan-library/library-node-content";
import { BasicTileNodeContainer } from "@iventis/plans/src/plan-library//basic-tile-node-container";
import { favouriteNodeServices, recentlyViewedNodeServices } from "@iventis/plans/src/services/library-api-functions";
import { StaticLibraryId, staticLibraries } from "@iventis/plans/src/plan-library/mapping-library.helpers";

type LibraryContainerProps = {
    libraryType: StaticLibraryId;
    onClick: () => void;
    onClickEmpty: () => void;
};

export const DashboardLibraryComponent: FunctionComponent<LibraryContainerProps> = ({ libraryType, onClickEmpty }) => {
    const [libraryTree, setLibraryTree] = useState<NodeWithHierarchy>(null);
    const [libraryThumbnails, setLibraryThumbnails] = useState<{ [nodeId: string]: string }>(null);

    useEffect(() => {
        const getLibraryTree = libraryType === StaticLibraryId.RecentlyViewed ? recentlyViewedNodeServices.getTree : favouriteNodeServices.getTree;
        getLibraryTree(null, null, null).then((res) => setLibraryTree(res as NodeWithHierarchy));
    }, []);

    useEffect(() => {
        const unmounted = { current: false };
        if (libraryTree && libraryThumbnails == null) {
            setThumbnails(libraryTree.childNodes, setLibraryThumbnails, unmounted);
        }
        return () => {
            unmounted.current = true;
        };
    }, [libraryTree]);

    const updateNode = (updatedNode: Node) => {
        const newLibraryTree = copyTree<Node, Node>(libraryTree);
        newLibraryTree.childNodes = newLibraryTree.childNodes.map((existingNode) => {
            if (existingNode.id === updatedNode.id) return updatedNode;
            return existingNode;
        });
        setLibraryTree(newLibraryTree);
    };

    return (
        <BasicTileNodeContainer
            tree={libraryTree}
            thumbnails={libraryThumbnails}
            content={(node: Node) => (
                <StandardNodeContent tile className="gallery-node-content" updatedAt={node.lastUpdatedAt} viewedAt={node.lastViewedAt} createdAt={node.createdAt} />
            )}
            staticLibrary={staticLibraries.find(({ id }) => id === libraryType)}
            updateNode={updateNode}
            onClickEmpty={onClickEmpty}
            includeNodeBreadcrumb
            isLoading={libraryTree == null}
        />
    );
};

function setThumbnails(
    childNodes: Node[],
    stateSetter: React.Dispatch<
        React.SetStateAction<{
            [nodeId: string]: string;
        }>
    >,
    unmounted: { current: boolean }
) {
    childNodes
        .filter((node) => node.type === NodeType.Map)
        .map((node) => recentlyViewedNodeServices.getNodeThumbnails[node.type](node))
        .forEach(async (thumbnailPromise) => {
            const thumbnail = await thumbnailPromise;
            if (unmounted.current) {
                return;
            }
            stateSetter((currentValue) => (currentValue == null ? thumbnail : { ...currentValue, ...thumbnail }));
        });
}
